import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import BeatLoader from 'react-spinners/BeatLoader'
import styled from 'styled-components'

import {
  IconPostPlayOutline,
} from 'assets/svg/sprite'
import { useGlobalVideo } from 'context/globalVideoContext'
import VideoSurface from 'context/VideoSurface'
import ProgressBar from 'features/VideoPlayer/ProgressBar/ProgressBar'
import { useVideoPlayedTracking } from 'helpers/hooks'
import { useLongPress } from 'helpers/hooks/useLongPress'
import { formatVideoTime } from 'lib/dayjs'
import { useUserProfile } from 'store'
import { usePreviewStore } from 'store/previewStore'
import type { CloudFrontCredentials } from 'helpers/cloudfrontCookies'

const TARGET_MAX_CROP = 0.26

interface PostWithVideoProps {
  url: string
  previewVideoUrl?: string | null
  videoId?: string
  videoUserId?: string
  shouldTrackPlayedS?: boolean
  updateSelectedPost: () => void
  thumbnail?: string
  previewThumbnail?: string | null
  thumbnailGenerating?: boolean
  onProgressSeconds?: (seconds: number) => void
  setIsPlaying?: (playing: boolean) => void
  videoDuration?: number
  width?: number | null
  height?: number | null
  segment_data: number[]
  isInViewport?: boolean
  cloudfront?: CloudFrontCredentials | null
  orientation?: string | null
  onOpenOptions?: () => void
  showPreviewVideo?: boolean
  hasPortraitMedia?: boolean
}

export const PostVideo = ({
  url,
  previewVideoUrl,
  shouldTrackPlayedS,
  videoId,
  videoUserId,
  updateSelectedPost,
  thumbnail,
  previewThumbnail,
  thumbnailGenerating = false,
  onProgressSeconds,
  setIsPlaying,
  videoDuration,
  segment_data,
  cloudfront,
  isInViewport = true,
  orientation,
  hasPortraitMedia = false,
  width,
  height,
  onOpenOptions,
  showPreviewVideo = false
}: PostWithVideoProps) => {
  const userData = useUserProfile()
  const previewOpen = usePreviewStore(state => state.previewOpen)

  const {
    activeMediaId,
    setActiveMediaId,
    play,
    pause,
    seekTo,
    getVideoElement
  } = useGlobalVideo()

  const mediaId = String(videoId ?? url)
  const playbackUrl =
    showPreviewVideo && previewVideoUrl
      ? previewVideoUrl
      : url
  const displayThumbnail =
    showPreviewVideo && previewThumbnail
      ? previewThumbnail
      : thumbnail
  const ownsSurface = activeMediaId === mediaId && !previewOpen

  const getMediaVideoElement = useCallback(
    () => getVideoElement(mediaId, playbackUrl),
    [getVideoElement, mediaId, playbackUrl]
  )

  const [playing, setPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [totalDuration, setTotalDuration] = useState(videoDuration ?? 0)
  const [hasPlayedOnce, setHasPlayedOnce] = useState(false)

  const isBlob = url.startsWith('blob:') || url.startsWith('data:video')
  const { updateMaxPlayedS, sendPlayedOnEnded } = useVideoPlayedTracking(
    shouldTrackPlayedS,
    url,
    videoId,
    videoUserId,
    totalDuration || videoDuration
  )

  const isPortrait =
    orientation === 'Portrait'

  const isLandscape =
    orientation === 'Landscape'

const containerRef =
  useRef<HTMLDivElement | null>(null)

const [containerRatio, setContainerRatio] =
  useState(0)

useLayoutEffect(() => {
  const container =
    containerRef.current

  if (!container) return

  const measure = () => {
    const width =
      container.clientWidth

    const height =
      container.clientHeight

    if (!width || !height) return

    const nextRatio =
      width / height

    setContainerRatio(previous =>
      Math.abs(previous - nextRatio) > 0.001
        ? nextRatio
        : previous
    )
  }

  measure()

  const observer =
    new ResizeObserver(measure)

  observer.observe(container)

  return () => {
    observer.disconnect()
  }
}, [])


const videoWidth =
  Number(width ?? 0)

const videoHeight =
  Number(height ?? 0)

const videoRatio =
  videoWidth > 0 &&
  videoHeight > 0
    ? videoWidth / videoHeight
    : 0

let adaptivePortrait = false
let foregroundWidth = 1

if (
  isPortrait &&
  videoRatio > 0 &&
  containerRatio > 0 &&
  videoRatio < containerRatio
) {
  const visibleFraction =
    videoRatio /
    containerRatio

  const cropFraction =
    1 - visibleFraction

  if (
    cropFraction >
    TARGET_MAX_CROP
  ) {
    foregroundWidth =
      Math.min(
        1,
        visibleFraction /
          (1 - TARGET_MAX_CROP)
      )

    adaptivePortrait = true
  }
}

  const isLandscapeVideoInPortraitPost =
    isLandscape &&
    hasPortraitMedia

  const orientationClass =
    isPortrait
      ? 'post__files__file--portrait'
      : isLandscape && !isLandscapeVideoInPortraitPost
        ? 'post__files__file--landscape'
        : ''

  useEffect(() => {
    if (videoDuration && videoDuration > 0) setTotalDuration(videoDuration)
  }, [videoDuration])

  useEffect(() => {
    const video = getMediaVideoElement()
    if (!video) return
    const syncState = () => {
      const duration =
        Number.isFinite(video.duration) && video.duration > 0
          ? video.duration
          : (videoDuration ?? 0)
      const time = video.currentTime || 0
      const isPlaying = !video.paused && !video.ended
      setPlaying(isPlaying)
      setIsPlaying?.(isPlaying)
      setCurrentTime(time)
      if (time > 0) {
        setHasPlayedOnce(true)
        onProgressSeconds?.(time)
      }
      if (duration > 0) setTotalDuration(duration)
    }
    const handleLoadedMetadata = () => {
      if (Number.isFinite(video.duration) && video.duration > 0) {
        setTotalDuration(video.duration)
      }
    }
    const handleTimeUpdate = () => {
      const time = video.currentTime || 0
      setCurrentTime(time)
      onProgressSeconds?.(time)
      if (!isBlob && shouldTrackPlayedS && time > 0) updateMaxPlayedS(time)
    }
    const handlePlay = () => {
      setPlaying(true)
      setIsPlaying?.(true)
      setHasPlayedOnce(true)
    }
    const handlePause = () => {
      setPlaying(false)
      setIsPlaying?.(false)
    }
    const handleEnded = () => {
      setPlaying(false)
      setIsPlaying?.(false)
      sendPlayedOnEnded()
    }
    syncState()
    video.addEventListener('loadedmetadata', handleLoadedMetadata)
    video.addEventListener('durationchange', handleLoadedMetadata)
    video.addEventListener('timeupdate', handleTimeUpdate)
    video.addEventListener('play', handlePlay)
    video.addEventListener('pause', handlePause)
    video.addEventListener('ended', handleEnded)
    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata)
      video.removeEventListener('durationchange', handleLoadedMetadata)
      video.removeEventListener('timeupdate', handleTimeUpdate)
      video.removeEventListener('play', handlePlay)
      video.removeEventListener('pause', handlePause)
      video.removeEventListener('ended', handleEnded)
    }
  }, [
    getMediaVideoElement,
    videoDuration,
    isBlob,
    shouldTrackPlayedS,
    onProgressSeconds,
    setIsPlaying,
    updateMaxPlayedS,
    sendPlayedOnEnded
  ])

  useEffect(() => {
    if (activeMediaId !== mediaId) return
    if (isInViewport || previewOpen) return
    pause()
    setPlaying(false)
    setIsPlaying?.(false)
    setActiveMediaId(null)
  }, [
    activeMediaId,
    mediaId,
    isInViewport,
    previewOpen,
    pause,
    setIsPlaying,
    setActiveMediaId
  ])

  useEffect(() => {
    if (activeMediaId === mediaId) return
    setPlaying(false)
    setIsPlaying?.(false)
  }, [activeMediaId, mediaId, setIsPlaying])

  const playVideo = () => {
    const video = getMediaVideoElement()

    if (
      video &&
      (
        video.ended ||
        (
          Number.isFinite(video.duration) &&
          video.duration > 0 &&
          video.currentTime >= video.duration - 0.1
        )
      )
    ) {
      video.currentTime = 0
    }

    if (activeMediaId !== mediaId) {
      setActiveMediaId(mediaId)
    }

    void play(mediaId, playbackUrl)
  }

  const pauseVideo = () => {
    if (activeMediaId !== mediaId) return
    pause()
  }

  const handleOnSeekChange = (fraction: number) => {
    if (!ownsSurface) return
    const seekFraction = fraction > 1 ? fraction / 100 : fraction
    seekTo(seekFraction, 'fraction')
  }

  const renderTimeOverlay = () => {
    const totalSecs = Math.round(
      totalDuration > 0 ? totalDuration : (videoDuration ?? 0)
    )
    if (!hasPlayedOnce) return formatVideoTime(totalSecs)
    return `${formatVideoTime(Math.round(currentTime))} / ${formatVideoTime(
      totalSecs
    )}`
  }

  const longPressHandlers = useLongPress({
    delay: 500,
    moveThreshold: 20,
    onLongPress: () => {
      onOpenOptions?.()
    }
  })

  useEffect(() => {
    setCurrentTime(0)
    setHasPlayedOnce(false)
  }, [playbackUrl])

  return (
    <VideoContainer
      ref={containerRef}
      className={[
        'post__files__file',
        orientationClass,

        adaptivePortrait
          ? 'post__files__file--adaptive-video-portrait'
          : '',

        isLandscapeVideoInPortraitPost
          ? 'post__files__file--landscape-video-in-portrait-post'
          : '',
      ]
        .filter(Boolean)
        .join(' ')}
      {...longPressHandlers}
      $foregroundWidth={foregroundWidth}
      onClick={event => {
        event.stopPropagation()
        if (playing) {
          pauseVideo()
          return
        }
        setActiveMediaId(mediaId)
        updateSelectedPost()
      }}
    >
      {(
      adaptivePortrait ||
          isLandscapeVideoInPortraitPost
        ) && displayThumbnail && (
        <img
          src={displayThumbnail}
          alt=''
          aria-hidden='true'
          draggable={false}
          className='post-video__blur'
        />
      )}

      <VideoSurface
        ownerType='post'
        ownerId={mediaId}
        mediaId={mediaId}
        videoId={videoId}
        videoUserId={videoUserId}
        url={playbackUrl}
        cloudfront={
          playbackUrl === url
            ? cloudfront
            : null
        }
        poster={displayThumbnail}
        active={ownsSurface}
        isFeedHost
        className='postVideo__videoSurface'
      />
      <div
        style={{ zIndex: 9 }}
        className='post__files__file__duration'
      >
        {renderTimeOverlay()}
      </div>
      {!playing && (
        <div
          style={{ zIndex: 9 }}
          className='post__files__file__play'
          onClick={event => {
            event.stopPropagation()
            playVideo()
          }}
        >
          <IconPostPlayOutline />
        </div>
      )}
      {thumbnailGenerating && (
        <ThumbnailGenerating>
          <BeatLoader size={5} color='#ffffff' />
          <span>Generating Thumb</span>
        </ThumbnailGenerating>
      )}
      {hasPlayedOnce && (
        <ProgressBarWrapper>
          <Progress>
            <ProgressBar
              getVideoElement={getMediaVideoElement}
              onSeek={handleOnSeekChange}
            />
          </Progress>
        </ProgressBarWrapper>
      )}
    </VideoContainer>
  )
}

const ProgressBarWrapper = styled.div`
  padding: 0 0.1rem;
  position: absolute;
  bottom: 0;
  width: 100%;
  z-index: 6;
  cursor: pointer;
`

const Progress = styled.div`
  width: 100%;
  margin-bottom: 0;
  progress {
    width: 100%;
  }
`

const ThumbnailGenerating = styled.div`
  position: absolute;
  top: calc(50% + 3.5rem);
  left: 50%;
  transform: translateX(-50%);
  z-index: 9;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.7rem;
  padding: 0.7rem 1rem;
  border-radius: 0.6rem;
  background: rgba(0, 0, 0, 0.62);
  backdrop-filter: blur(0.3rem);
  -webkit-backdrop-filter: blur(0.3rem);
  color: #bfbfbf;
  font-size: 1.2rem;
  line-height: 1;
  white-space: nowrap;
  pointer-events: none;
`
const VideoContainer = styled.div<{
  $foregroundWidth: number
}>`
  --portrait-foreground-width: ${({
    $foregroundWidth,
  }) =>
    `${$foregroundWidth * 100}%`};
`